import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  BirthObservationsLoadStatus,
  LaborStagesLoadStatus,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock-ready";
import {
  defaultLaborAmendFlowsheetPersistContextValue,
  type LaborAmendFlowsheetPersistContextValue,
} from "@/encounters/contexts/labor-amend-flowsheet-persist/types";
import { useLaborAmendFlowsheetPersistState } from "@/encounters/contexts/labor-amend-flowsheet-persist/use-labor-amend-flowsheet-persist-state";

export type {
  EnqueueFlowsheetPersistOptions,
  LaborAmendFlowsheetFlushResult,
  LaborAmendFlowsheetPersistContextValue,
  LaborAmendPendingDisplayEntry,
  LaborAmendPendingDisplayEntryType,
} from "@/encounters/contexts/labor-amend-flowsheet-persist/types";

export const LaborAmendFlowsheetPersistContext = createContext(
  defaultLaborAmendFlowsheetPersistContextValue,
);

export { defaultLaborAmendFlowsheetPersistContextValue };

export function LaborAmendFlowsheetPersistProvider({
  children,
  encounterId,
  initialEncounterQuestionnaireParentVersionId,
  isFlowsheetEditingEnabled = true,
  laborStagesLoadStatus = "ready",
  birthObservationsLoadStatus = "ready",
  onDiscardAmendWork,
}: {
  children: ReactNode;
  encounterId: string;
  /** Baseline encounter QR `meta.versionId` when amend opens (Sign & Lock snapshot). */
  initialEncounterQuestionnaireParentVersionId?: string;
  isFlowsheetEditingEnabled?: boolean;
  laborStagesLoadStatus?: LaborStagesLoadStatus;
  birthObservationsLoadStatus?: BirthObservationsLoadStatus;
  /** Clears optimistic amend-only client state (for example Redux birth) when work is discarded. */
  onDiscardAmendWork?: () => void;
}) {
  const encounterQuestionnaireParentVersionIdRef = useRef<string | undefined>(
    initialEncounterQuestionnaireParentVersionId,
  );
  const [encounterQuestionnaireParentVersionId, setEncounterQuestionnaireParentVersionIdState] =
    useState<string | undefined>(initialEncounterQuestionnaireParentVersionId);

  useEffect(() => {
    encounterQuestionnaireParentVersionIdRef.current = initialEncounterQuestionnaireParentVersionId;

    setEncounterQuestionnaireParentVersionIdState(initialEncounterQuestionnaireParentVersionId);
  }, [initialEncounterQuestionnaireParentVersionId]);

  const setEncounterQuestionnaireParentVersionId = useCallback((versionId: string | undefined) => {
    encounterQuestionnaireParentVersionIdRef.current = versionId;
    setEncounterQuestionnaireParentVersionIdState(versionId);
  }, []);

  /** Reads the ref so Sign & Lock flush sees the version set in the same tick (before React re-render). */
  const getEncounterQuestionnaireParentVersionIdForStamp = useCallback(
    () => encounterQuestionnaireParentVersionIdRef.current,
    [],
  );

  const { persistQueue, pendingPreviews } = useLaborAmendFlowsheetPersistState({
    encounterId,
    onDiscardAmendWork,
  });

  const value = useMemo<LaborAmendFlowsheetPersistContextValue>(
    () => ({
      deferFlowsheetPersists: true,
      isFlowsheetEditingEnabled,
      laborStagesLoadStatus,
      birthObservationsLoadStatus,
      isFlowsheetPersistLocked: persistQueue.isFlowsheetPersistLocked,
      hasCommittedFlowsheetToServer: persistQueue.hasCommittedFlowsheetToServer,
      enqueueFlowsheetPersist: persistQueue.enqueueFlowsheetPersist,
      flushFlowsheetPersistQueue: persistQueue.flushFlowsheetPersistQueue,
      drainFlowsheetPersistQueue: persistQueue.drainFlowsheetPersistQueue,
      commitAmendLocalPreviews: persistQueue.commitAmendLocalPreviews,
      discardFlowsheetPersistQueue: persistQueue.discardFlowsheetPersistQueue,
      setSignAndLockInProgress: persistQueue.setSignAndLockInProgress,
      registerPostAmendmentEffect: persistQueue.registerPostAmendmentEffect,
      runPostAmendmentEffects: persistQueue.runPostAmendmentEffects,
      finalizePostAmendmentEffects: persistQueue.finalizePostAmendmentEffects,
      pendingDisplayEntries: pendingPreviews.pendingDisplayEntries,
      registerPendingDisplayEntry: pendingPreviews.registerPendingDisplayEntry,
      getPendingEntryLaborStageLoincCode: pendingPreviews.getPendingEntryLaborStageLoincCode,
      setObservationPreviewPatch: pendingPreviews.setObservationPreviewPatch,
      getObservationPreviewPatch: pendingPreviews.getObservationPreviewPatch,
      applyObservationAmendPreview: pendingPreviews.applyObservationAmendPreview,
      hasLocalAmendPreviews: pendingPreviews.hasLocalAmendPreviews,
      hasLostPendingWorkAfterRefresh: persistQueue.hasLostPendingWorkAfterRefresh,
      hasLostPostAmendmentEffectsAfterRefresh: persistQueue.hasLostPostAmendmentEffectsAfterRefresh,
      getLaborStageObservationsForFlush: persistQueue.getLaborStageObservationsForFlush,
      encounterQuestionnaireParentVersionId,
      setEncounterQuestionnaireParentVersionId,
      getEncounterQuestionnaireParentVersionIdForStamp,
      canExecuteDeferredFlowsheetPersist: persistQueue.canExecuteDeferredFlowsheetPersist,
      registerAmendProvenanceTarget: persistQueue.registerAmendProvenanceTarget,
      registerAmendStageMembershipChange: persistQueue.registerAmendStageMembershipChange,
      peekAmendProvenanceSnapshot: persistQueue.peekAmendProvenanceSnapshot,
      finalizeAmendProvenanceSnapshot: persistQueue.finalizeAmendProvenanceSnapshot,
    }),
    [
      isFlowsheetEditingEnabled,
      laborStagesLoadStatus,
      birthObservationsLoadStatus,
      persistQueue,
      pendingPreviews,
      encounterQuestionnaireParentVersionId,
      setEncounterQuestionnaireParentVersionId,
      getEncounterQuestionnaireParentVersionIdForStamp,
    ],
  );

  return (
    <LaborAmendFlowsheetPersistContext.Provider value={value}>
      {children}
    </LaborAmendFlowsheetPersistContext.Provider>
  );
}

export function useLaborAmendFlowsheetPersist(): LaborAmendFlowsheetPersistContextValue {
  return useContext(LaborAmendFlowsheetPersistContext);
}
